import React from 'react'
import { CloudinaryAdvancedImage } from '../Misc/CloudinaryImage'
import styled from '@emotion/styled'
import { colors } from '../../../tokens/colors'

type Props = {
  /**
   * The cloudinary id for the header logo
   */
  logoCloudinaryId?: string
  /**
   * The nonprofits name
   */
  nonprofitName: string
  /**
   * The url the header logo should link to
   */
  websiteUrl?: string
  /**
   * The nonprofits name
   */
  themeColor?: string
}

/**
 * StoryHeader Component
 *
 * Display the nonprofit's logo, or name at the top of a public story.
 * Will contain TOC menu in the future.
 *
 * @param {Props} props - The component props.
 * @returns {JSX.Element} - A React functional component representing the StoryHeader.
 */
export const StoryHeader = ({
  logoCloudinaryId,
  nonprofitName,
  themeColor,
  websiteUrl,
}: Props): JSX.Element => (
  <Header themeColor={themeColor}>
    <StyledAnchor href={websiteUrl} target='_blank' rel='noreferrer'>
      {logoCloudinaryId ? (
        <CloudinaryAdvancedImage
          imagePublicId={logoCloudinaryId}
          imageAltText={nonprofitName}
        />
      ) : (
        <div>{nonprofitName}</div>
      )}
    </StyledAnchor>
  </Header>
)

const Header = styled('header')<{ themeColor?: string }>`
  border-top: 8px solid ${({ themeColor }) => themeColor || '#47855C'};
  box-shadow: 2px 4px 8px 0px rgba(0, 50, 99, 0.07);
  padding: 18px 22px;
  background-color: ${colors.background.secondaryBackground};
  position: fixed;
  z-index: 4;
  width: 100%;
  height: 60px;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  img {
    max-height: 60px;
  }
`

const StyledAnchor = styled('a')`
  font-size: 24px;
  line-height: 135%;
  font-weight: 600;
  font-family: Source Sans pro;
  color: ${colors.copy.paragraphDark};
  text-decoration: none;
`
